import { closedToClientsWhy, type ContactDto } from '@nsb/core';
import { useState } from 'react';
import { api, errorText, queryClient, useApi } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { Button, Confirm, ErrorLine, Field, LoadError, Loading, Page, TextInput, TextLink, Title } from '../../components/ui.tsx';
import { useUnsaved } from '../../components/useUnsaved.ts';
import { isAdmin, useMe } from '../../session.tsx';
import { peopleChanges } from '../formChanges.ts';
import { ProjectCrumb } from './common.tsx';
import { useProject, useProjectId } from './hooks.ts';
import { dateWords } from '../../days.ts';
import { PASSWORD_MIN, PASSWORD_RULE } from '../passwordForm.ts';

interface PeopleDto {
  contacts: ContactDto[];
  /** Whether the portal's own emails can go out: through Resend, or Gmail while Resend is not connected. */
  mailConnected: boolean;
}

interface Row {
  id?: string;
  name: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

interface Invited {
  contactId: string;
  email: string | null;
  status: 'sent' | 'not_sent' | 'failed' | 'no_email';
  error: string | null;
  devLink?: string;
}

const rowsOf = (contacts: ContactDto[]): Row[] => contacts.map((c) => ({ id: c.id, name: c.name, email: c.email ?? '', phone: c.phone ?? '', isPrimary: c.isPrimary }));

// Saved as it stands: nothing the save would change (screens/formChanges.ts, the rule the bar of changes not saved names).
const same = (rows: Row[], contacts: ContactDto[]) => peopleChanges(contacts, rows).length === 0;

export function People() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const { data: me } = useMe();
  const { data, error } = useApi<PeopleDto>(['project', projectId, 'people'], `/projects/${projectId}/people`);
  useFeedbackRecord(projectId, project ? { type: 'people', id: projectId, label: `${project.name} — people` } : null);
  if (error) return <LoadError error={error} />;
  if (!data || !project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  return (
    <Page wide>
      <ProjectCrumb project={project} />
      <Title>People</Title>
      {/* Fresh once the people themselves change (a save): an invitation going out leaves what is being typed alone. */}
      <PeopleForm
        key={JSON.stringify(data.contacts.map((c) => [c.id, c.name, c.email, c.phone, c.isPrimary]))}
        projectId={projectId}
        data={data}
        admin={isAdmin(me)}
        noClientSide={closedToClientsWhy(project)}
      />
    </Page>
  );
}

/**
 * The project's people, each with their own controls (B39; Jason, 2026-10-07): Primary chosen among them and saved
 * with the rest, Take off asked first; Set a password an admin's; invitations only where the job has a client side
 * (core `closedToClientsWhy`: never a lead or NSB's own job, the rule View as follows).
 */
export function PeopleForm({ projectId, data, admin, noClientSide }: { projectId: string; data: PeopleDto; admin: boolean; noClientSide: string | null }) {
  const [rows, setRows] = useState<Row[]>(() => rowsOf(data.contacts));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [passwordFor, setPasswordFor] = useState<string | null>(null);
  // Who is being taken off: asked first, then gone.
  const [takingOff, setTakingOff] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  // Who to invite (Jason, 2026-10-04: "select project people, and they'll get an email with a log in link that
  // requests they set a password"): the people saved with an email, ticked, invited together.
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  // NSB's note at the top of the email (P-27, #109): the same for everyone ticked, kept with what was sent.
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [invited, setInvited] = useState<Invited[] | null>(null);
  const byId = new Map(data.contacts.map((c) => [c.id, c]));
  const unsaved = !same(rows, data.contacts);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/projects/${projectId}/contacts`, {
        method: 'PUT',
        // A new row left blank is dropped; someone already on the project comes off only by Take off.
        body: { contacts: rows.filter((r) => r.id || r.name.trim()).map((r) => ({ ...r, email: r.email.trim() || null })) },
      });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const takeOff = async (id: string) => {
    setRemoving(true);
    setErr(null);
    try {
      await api(`/projects/${projectId}/contacts/${id}`, { method: 'DELETE' });
      setTakingOff(null);
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setRemoving(false);
    }
  };
  const invite = async () => {
    setSending(true);
    setErr(null);
    setInvited(null);
    try {
      const r = await api<{ invited: Invited[] }>(`/projects/${projectId}/invites`, { body: { contactIds: [...chosen], note: note.trim() || null } });
      setInvited(r.invited);
      setChosen(new Set());
      // Gone with every invitation it went in; kept while one did not go, to send again in the same words.
      if (r.invited.every((v) => v.status === 'sent' || v.status === 'no_email')) setNote('');
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSending(false);
    }
  };
  const pick = (id: string, on: boolean) => {
    const next = new Set(chosen);
    if (on) next.add(id);
    else next.delete(id);
    setChosen(next);
  };

  // Changes not saved, on the bar at the window's foot, which saves as Save people does (P-29).
  useUnsaved({ names: peopleChanges(data.contacts, rows), busy, error: err, onSave: save, onDiscard: () => (setRows(rowsOf(data.contacts)), setErr(null)) });
  return (
    <>
      {!data.mailConnected ? (
        <p className="mb-6 text-sm text-ink-2">
          Invitations go out by email, and the portal’s email is not connected yet. <TextLink to="/settings/connections">Connect Resend or Gmail in Settings</TextLink>.
        </p>
      ) : null}
      {/* What each field is (Jason, 2026-10-04: "what are these two fields? we need some helper text here"). */}
      <p className="mb-4 max-w-measure text-ink-2">
        The client and anyone on their side of the project. An email address is what an invitation to the portal goes to; the phone is for NSB to reach them.
      </p>
      <div className="hidden gap-3 pb-2 sm:flex" aria-hidden="true">
        <span className="w-6 shrink-0" />
        <div className="min-w-0 flex-1 xl:grid xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] xl:gap-x-10">
          <div className="grid gap-x-4 sm:grid-cols-[1.2fr_1.5fr_1fr]">
            <span className="label">Name</span>
            <span className="label">Email, for the invitation</span>
            <span className="label">Phone</span>
          </div>
          <span className="label hidden xl:block">In the portal</span>
        </div>
      </div>
      <ul className="divide-y divide-rule border-y border-rule">
        {rows.map((r, i) => {
          const c = r.id ? byId.get(r.id) : undefined;
          return (
            // A wide screen reads each person across: their details, then where they stand and what can be done.
            <li key={r.id ?? `n${i}`} className="flex gap-3 py-3">
              {/* Ticked to invite: someone saved, with an email to send it to. */}
              <label className="flex min-h-11 w-6 shrink-0 items-start justify-center pt-3.5" title={noClientSide ?? (c?.email ? `Invite ${c.name}` : c ? 'No email address to send to' : 'Save them first')}>
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  aria-label={c ? `Invite ${c.name}` : 'Save this person first'}
                  disabled={!c?.email || noClientSide !== null}
                  checked={c ? chosen.has(c.id) : false}
                  onChange={(e) => c && pick(c.id, e.target.checked)}
                />
              </label>
              <div className="min-w-0 flex-1 xl:grid xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] xl:items-center xl:gap-x-10">
                <div className="grid gap-x-4 sm:grid-cols-[1.2fr_1.5fr_1fr]">
                  <TextInput aria-label="Name" placeholder="Name" value={r.name} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                  <TextInput aria-label="Email" type="email" placeholder="Email, to invite them" value={r.email} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} />
                  <TextInput aria-label="Phone" type="tel" placeholder="Phone" value={r.phone} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))} />
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-5 text-sm text-ink-3 xl:mt-0">
                  {c?.lastLoginAt ? (
                    <span>Last signed in {dateWords(c.lastLoginAt, 'date')}</span>
                  ) : c?.hasPassword ? (
                    <span>Not signed in yet</span>
                  ) : c?.invitedAt ? (
                    <span>Invited {dateWords(c.invitedAt, 'date')}</span>
                  ) : (
                    <span>Not invited</span>
                  )}
                  {c?.hasPassword ? <span>{c.mustChangePassword ? 'Temporary password set' : 'Has their own password'}</span> : null}
                  <label className="flex min-h-11 items-center gap-2 text-ink-2">
                    <input type="radio" name="primary" className="h-4 w-4" checked={r.isPrimary} onChange={() => setRows(rows.map((x, j) => ({ ...x, isPrimary: j === i })))} />
                    Primary
                  </label>
                  {admin && !noClientSide && c && c.email ? (
                    <button type="button" className="link min-h-11" onClick={() => setPasswordFor(passwordFor === c.id ? null : c.id)}>
                      {c.hasPassword ? 'Reset password' : 'Set a password'}
                    </button>
                  ) : null}
                  {c ? (
                    <button
                      type="button"
                      className="link min-h-11 disabled:no-underline disabled:opacity-60"
                      disabled={unsaved}
                      title={unsaved ? 'Save the people first.' : undefined}
                      onClick={() => setTakingOff(takingOff === c.id ? null : c.id)}
                    >
                      Take off
                    </button>
                  ) : (
                    <button type="button" className="link min-h-11" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                      Take off
                    </button>
                  )}
                </div>
                {c && takingOff === c.id ? (
                  <div className="xl:col-span-full">
                    <Confirm
                      prompt={`Take ${c.name} off the project? Their sign-in to it ends, and any review they were asked to do stops waiting on them.`}
                      busy={removing}
                      onYes={() => void takeOff(c.id)}
                      onNo={() => setTakingOff(null)}
                    />
                  </div>
                ) : null}
                {c && passwordFor === c.id ? (
                  <div className="max-w-column xl:col-span-full">
                    <PasswordPanel projectId={projectId} contact={c} onDone={() => setPasswordFor(null)} />
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-3">
        <Button tone="quiet" onClick={() => setRows([...rows, { name: '', email: '', phone: '', isPrimary: rows.length === 0 }])}>
          Add a person
        </Button>
      </div>
      <ErrorLine error={err} />
      <div className="mt-6">
        <Button onClick={() => void save()} busy={busy}>
          Save people
        </Button>
      </div>
      <section className="mt-12 max-w-measure border-t border-rule pt-4">
        <h2 className="text-[1.375rem]">Invite them to the portal</h2>
        {noClientSide ? (
          <p className="mt-1 text-ink-2">{noClientSide}</p>
        ) : (
          <>
            <p className="mt-1 text-ink-2">
              Tick who to invite. Each gets an email from you with a link that signs them in and asks them to choose a password; the link works for fourteen days. Inviting someone again sends them a new link.
            </p>
            <div className="mt-4">
              <Field label="A note with the invitation" hint="Optional. It goes at the top of the email, in your words.">
                {(id) => <textarea id={id} rows={3} maxLength={1000} className="field mt-1 resize-y" value={note} onChange={(e) => setNote(e.target.value)} />}
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <Button busy={sending} disabled={!chosen.size || unsaved} onClick={() => void invite()}>
                {chosen.size > 1 ? `Send ${chosen.size} invitations` : 'Send the invitation'}
              </Button>
              <span className="meta">{unsaved ? 'Save the people first.' : chosen.size ? `${chosen.size} ticked` : 'Nobody ticked yet.'}</span>
            </div>
          </>
        )}
        {invited ? (
          <ul className="mt-4 divide-y divide-rule border-y border-rule text-[0.9375rem]">
            {invited.map((v) => {
              const who = byId.get(v.contactId)?.name ?? v.email ?? 'Someone';
              return (
                <li key={v.contactId} className="py-2">
                  {v.status === 'sent' ? `Sent to ${who}${v.email ? `, ${v.email}` : ''}.` : v.status === 'no_email' ? `${who} has no email address.` : `Not sent to ${who}: ${v.error ?? 'it did not go'}.`}
                  {v.devLink ? (
                    <span className="meta block">
                      On this machine: <a className="link" href={v.devLink}>{who}’s link</a>. Open it in a private window: it signs that window in as them.
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </>
  );
}

const WORDS = ['cedar', 'ridge', 'sugi', 'hemlock', 'maple', 'stone', 'timber', 'river', 'meadow', 'birch', 'ember', 'harbor', 'summit', 'willow'];

/** A temporary password that reads aloud and types easily on a phone: two words and four digits. */
function makePassword(): string {
  const r = crypto.getRandomValues(new Uint32Array(3));
  const word = (n: number) => WORDS[n % WORDS.length]!;
  const cap = (w: string) => w[0]!.toUpperCase() + w.slice(1);
  return `${cap(word(r[0]!))}-${cap(word(r[1]!))}-${String(r[2]! % 10_000).padStart(4, '0')}`;
}

/**
 * A sign-in set by hand, before email is wired: a temporary password the
 * client changes the first time they sign in. Setting it again resets it and
 * signs them out everywhere. Afterwards, a message ready to send them.
 */
function PasswordPanel({ projectId, contact, onDone }: { projectId: string; contact: ContactDto; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const save = async () => {
    // The rule in words, never a button held back (L-13: the same password practice as Account).
    if (password.trim().length < PASSWORD_MIN) {
      setErr(password === '' ? 'Fill in a temporary password, or press Make one.' : 'Too short: a password needs at least ten characters.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api(`/projects/${projectId}/contacts/${contact.id}/password`, { body: { password } });
      const first = contact.name.split(/\s+/)[0] ?? contact.name;
      setSent(
        `Hi ${first}, your project page is ready: ${window.location.origin}/login?with=password\n` +
          `Sign in with ${contact.email} and the temporary password ${password}. ` +
          `You'll choose your own the first time you sign in.`,
      );
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (sent) {
    return (
      <div className="mt-3 border border-rule bg-sheet p-4">
        <p className="label mb-2">Send them this</p>
        <p className="whitespace-pre-line text-[0.9375rem]">{sent}</p>
        <div className="mt-3 flex gap-5">
          <Button
            tone="secondary"
            onClick={() =>
              void navigator.clipboard.writeText(sent).then(
                () => setCopied(true),
                () => setCopied(false),
              )
            }
          >
            {copied ? 'Copied' : 'Copy the message'}
          </Button>
          <Button
            tone="quiet"
            onClick={() => {
              // Only now: the refreshed list re-keys the form, which would take this message with it.
              onDone();
              void queryClient.invalidateQueries({ queryKey: ['project', projectId, 'people'] });
            }}
          >
            Done
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3 border border-rule bg-sheet p-4">
      <p className="text-sm text-ink-2">
        {contact.hasPassword ? 'A new temporary password signs them out everywhere. ' : ''}They sign in with {contact.email} and choose their own password the first time.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-x-5 gap-y-3">
        <label className="block min-w-0 flex-1">
          <span className="label block">Temporary password</span>
          <TextInput value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" aria-describedby={`pw-rule-${contact.id}`} />
          <span id={`pw-rule-${contact.id}`} className="mt-1 block text-sm text-ink-2">
            {PASSWORD_RULE}
          </span>
        </label>
        <Button tone="quiet" onClick={() => setPassword(makePassword())}>
          Make one
        </Button>
      </div>
      <div className="mt-3 flex gap-5">
        <Button busy={busy} onClick={() => void save()}>
          {contact.hasPassword ? 'Reset it' : 'Set it'}
        </Button>
        <Button tone="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}

